// Card 03, the compass rose (R548, R1745, ui-map-interface.md §17.3).
//
// A rose stands in a corner of every chart. This one stands in the
// console's own `rose` slot (R555), north up as the world is, and carries
// the NEEDLE, which points along the bearing to the commander's own hold,
// so a reader who has panned off it knows the way home. It is the one
// solid mark of the rose, in the bright accent, and its bearing stands
// under the rim in the same ink. It is the needle of the rose of mockup
// round 082 (R1771): a head and a faint tail, each a plain triangle with
// its base on the hub.
//
// R1745 drew both readings again in the hairline family of the chrome
// (mockup rounds 080 and 082). Both stand on a ground of the deep ink
// inside one hairline rim. The historical reading is an eight-point star
// with the fleur over north and the bearing as a point of the compass in
// the Fell; the modern one is a tick ring, the N and the bearing as a
// figure in the mono face.
//
// **The rose has two forms** (R1801, card 17 b of mockup round 086). While
// the minimap is open, the rose is the frame of the ROUND MINIMAP: the
// console gives the chart a disc in the centre of the rose's slot
// (`clusterBox("map")`), and the rose draws its rim, its ticks, a needle
// MARK on the rim and the five arcs of the sea share round the disc. A
// needle across the dial would cover the chart. While the minimap is off,
// the rose stands alone in its own smaller slot, as before.
//
// **The rose is a visible control** (R1802, card 16 of mockup round 086).
// A press on it centres the map on the hold (R1758). The rose is canvas,
// so the press is a transparent DOM button over its slot, which the
// overlay owns; this piece draws the control. At rest a home glyph stands
// in the dim ink; a hover (`ui/rosepress.ts`) lights a ring of the accent
// and the glyph; a press fills the dial and lays a bright ring on the rim
// that grows and fades over `press_sec`. The variation arrow and its EDGE
// line of R585 are gone (R1802).
//
// **It is a SCREEN piece.** Every instance is placed at the world point
// under a screen position and carries `F_SCREEN`, so the rose holds one
// size at any zoom. Every offset below is CSS px about the rose's centre,
// which is a world point. Every word is a screen word at its chart text
// step (R1796).

import { F_SCREEN } from '../../layer';
import type { FrameInfo } from '../../layer';
import { chart as S } from '../../../gen/strings';
import { NUM_FACTIONS } from '../../../gen/balance';
import { bearingDeg, compassPoint, formatBearing } from '../../../render/bearing';
import { reducedMotion } from '../../../render/motion';
import { clusterBox } from '../../../ui/layout';
import { roseHovered, rosePressCount } from '../../../ui/rosepress';
import { factionColor } from '../../../world';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { stepPx, type ChartStep } from '../steps';
import type { ChartFace } from '../../gfx/atlas';
import { CHART_INK, faceOf, inkOf } from './shared';

/** The eight points of the historical star: four cardinal, four between. */
const POINTS = 8;
/** The turn from one point of the star to the next. */
const STEP = (2 * Math.PI) / POINTS;
/** A bearing of 0 points up the screen, which is −90° on the screen's own
 *  angle (0 at the right, growing clockwise, because y grows down). */
const NORTH = -Math.PI / 2;
/** The strokes of the home glyph: two of the roof, two walls, the floor. */
export const HOME_STROKES = 5;

/** The screen angle of a compass bearing in degrees. */
function screenAngle(deg: number): number {
  return NORTH + (deg * Math.PI) / 180;
}

/**
 * Where the rim stands in a slot of `w` by `h` CSS px (R1745): its radius,
 * and its centre's distance from the slot's top edge.
 *
 * `top` and `bottom` are the room the words need over and under the rim.
 * The radius is half of what the slot's height leaves, and the block of
 * rim and words is centred in the slot, so a slot taller than it is wide
 * loses no word. A slot too small for the words answers a radius of 0 or
 * less, and the piece then draws nothing.
 */
export function roseRim(
  w: number, h: number, top: number, bottom: number,
): { r: number; cy: number } {
  const r = (Math.min(w, h) - top - bottom) / 2;
  const spare = h - (top + 2 * r + bottom);
  return { r, cy: spare / 2 + top + r };
}

/**
 * How much of the answer to a press stands `age` seconds after the press
 * (R1758): 1 at the press, falling in a straight line to 0 at `life`
 * seconds, and 0 before any press and after its life.
 */
export function pressFade(age: number, life: number): number {
  if (!(age >= 0) || !(life > 0) || age >= life) return 0;
  return 1 - age / life;
}

/**
 * How many strokes of width `w` fill a solid triangle whose base is
 * `base` px wide (R1745). The outer two strokes stand half a width inside
 * the two base corners, so the fan covers the base and no more, and no two
 * neighbours stand further apart than one width, so the fan has no gap.
 */
export function fanLines(base: number, w: number): number {
  const span = Math.max(0, base - w);
  return span > 0 ? Math.ceil(span / w) + 1 : 1;
}

/**
 * The share of the held water of each faction, 1 to `factions` (R1801):
 * entry `f - 1` is the part of every held cell that faction `f` holds, so
 * the entries add up to 1. A sea where nobody holds water answers zeros.
 * The arcs of the round minimap take these parts of the ring.
 */
export function seaShares(owners: Uint8Array, factions: number = NUM_FACTIONS): number[] {
  const count = new Array<number>(factions).fill(0);
  let total = 0;
  for (let i = 0; i < owners.length; i++) {
    const o = owners[i];
    if (o < 1 || o > factions) continue;
    count[o - 1]++;
    total++;
  }
  return total > 0 ? count.map((c) => c / total) : count;
}

export class CompassRose implements Piece {
  readonly id = 'compass_rose' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'owners', 'flip', 'reading'] as const;

  /** The bearing to the commander's own hold, in degrees. */
  private home = 0;
  /** The sea share of each faction at the last rebuild (R1801). */
  private shares: number[] = [];
  /** The presses of the rose press this piece has answered (R1758). */
  private pressSeen = rosePressCount();
  /** When the last press arrived, on the frame clock, and whether reduced
   *  motion stood then: it is read once a press, not on every frame. */
  private pressAt = -Infinity;
  private pressStill = false;

  /** A rebuild counts the sea share again: a flip moves it. */
  rebuild(ctx: ChartContext): void { this.shares = seaShares(ctx.scene.owners); }

  update(ctx: ChartContext, frame: FrameInfo): void {
    // A new press of the rose press: its answer starts on this frame.
    const presses = rosePressCount();
    if (presses !== this.pressSeen) {
      this.pressSeen = presses;
      this.pressAt = frame.timeS;
      this.pressStill = reducedMotion();
    }
    const cx = (ctx.view.x0 + ctx.view.x1) / 2;
    const cy = (ctx.view.y0 + ctx.view.y1) / 2;
    // The needle is one distance: every frame, so it turns with the pan.
    this.home = bearingDeg(cx, cy, ctx.scene.home.x, ctx.scene.home.y);
  }

  emit(sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo): void {
    // The slot. The console hands back an empty box before its first
    // layout, and the rose draws nothing rather than guess a place.
    const box = clusterBox('rose');
    if (!box.visible || box.w <= 0 || box.h <= 0) return;
    const glow = pressFade(frame.timeS - this.pressAt, ctx.cfg.pieces.compass_rose.fig.press_sec);
    const disc = clusterBox('map');
    if (disc.visible && disc.w > 0) this.round(sinks, ctx, frame, box, disc, glow);
    else this.alone(sinks, ctx, frame, box, glow);
  }

  // -- the two forms -------------------------------------------------------

  /** The rose alone in its own slot: the minimap is off. */
  private alone(
    sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo,
    box: { x: number; y: number; w: number; h: number }, glow: number,
  ): void {
    const t = ctx.cfg.pieces.compass_rose;
    const f = t.fig;
    const z = ctx.zoom;
    const sheet = ctx.sheet;
    const rim = roseRim(
      box.w, box.h, sheet ? f.rim_top_sheet : f.rim_top, sheet ? f.rim_bottom_sheet : f.rim_bottom,
    );
    const R = rim.r;
    if (R <= 0) return;
    // The world point under the rim's centre, which is where every
    // instance of a screen piece is written (`fast/view.ts`).
    const cx = ctx.view.x0 + (box.x + box.w / 2) / z;
    const cy = ctx.view.y0 + (box.y + rim.cy) / z;

    // The ground, so the hairlines and the words read over any water.
    const ground = ctx.reading === 'historical' ? t.historical.fig.ground_alpha : t.modern.fig.ground_alpha;
    sinks.circles.disc(cx, cy, R, CHART_INK.deep, ground, F_SCREEN);
    // A press fills the dial (R1802), and the fill fades with the ring.
    if (glow > 0) sinks.circles.disc(cx, cy, R, CHART_INK.accent, f.press_fill_alpha * glow, F_SCREEN);

    if (ctx.reading === 'historical') this.historical(sinks, ctx, cx, cy, R, z);
    else this.modern(sinks, ctx, cx, cy, R, z);

    this.answer(sinks, cx, cy, R, R + f.hover_dr, f, glow);
    // The home glyph in the lower half of the dial, under the needle.
    const size = Math.max(f.home_min_px, R * f.home_size_k);
    homeGlyph(sinks, cx, cy, z, 0, R * f.home_k, size, f, glow > 0 || roseHovered());

    // The needle, over both readings: the one solid mark, in the bright
    // accent. It is the needle of mockup round 082 (R1771): two plain
    // triangles on one base across the hub. The faint tail draws first,
    // so the head to the hold is the last fan the rose packs.
    const a = screenAngle(this.home), w = needleFill(f.needle_fill_w, frame.cam.dpr);
    solid(sinks, cx, cy, z, a + Math.PI, R * f.needle_k, 0, R * f.needle_half_k, w, CHART_INK.bright, f.needle_tail_alpha);
    solid(sinks, cx, cy, z, a, R * f.needle_k, 0, R * f.needle_half_k, w, CHART_INK.bright, f.needle_alpha);
  }

  /**
   * The frame of the round minimap (R1801): the rim round the disc, the
   * ticks in the ring between them, the five arcs of the sea share, the
   * words in the free corners of the slot and a needle mark on the rim.
   */
  private round(
    sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo,
    box: { x: number; y: number; w: number; h: number },
    disc: { x: number; y: number; w: number; h: number }, glow: number,
  ): void {
    const t = ctx.cfg.pieces.compass_rose;
    const f = t.fig;
    const z = ctx.zoom;
    const hist = ctx.reading === 'historical';
    const D = disc.w / 2;
    const R = D * (1 + f.round_rim_k);
    const band = R - D;
    const half = box.w / 2;
    const cx = ctx.view.x0 + (disc.x + D) / z;
    const cy = ctx.view.y0 + (disc.y + D) / z;

    // The ground under the ring; the chart's canvas stands over the disc.
    const ground = hist ? t.historical.fig.ground_alpha : t.modern.fig.ground_alpha;
    sinks.circles.disc(cx, cy, R, CHART_INK.deep, ground, F_SCREEN);
    // A press fills the ring between the disc and the rim (R1802).
    if (glow > 0) {
      sinks.circles.ring(cx, cy, (R + D) / 2, band, CHART_INK.accent, f.press_fill_alpha * glow, F_SCREEN);
    }

    // The rim, and the ticks of the modern reading inside the ring.
    if (hist) {
      const r = t.historical;
      sinks.circles.ring(cx, cy, R, r.width, inkOf(r.ink, CHART_INK.pale), r.alpha, F_SCREEN);
    } else {
      const r = t.modern, m = r.fig;
      const ink = inkOf(r.ink, factionColor(ctx.state.faction));
      sinks.circles.ring(cx, cy, R, r.width, ink, r.alpha, F_SCREEN);
      for (let k = 0; k < m.ticks; k++) {
        const a = NORTH + (k * 2 * Math.PI) / m.ticks;
        const major = k % m.major_every === 0;
        const inner = R - band * (major ? f.round_tick_major_k : f.round_tick_k);
        const cos = Math.cos(a), sin = Math.sin(a);
        line(sinks, cx, cy, z, cos * R, sin * R, cos * inner, sin * inner,
          r.width, ink, major ? m.tick_major_alpha : m.tick_alpha);
      }
    }

    // The sea share: one arc per faction, from north clockwise.
    const gap = (f.share_gap_deg * Math.PI) / 180;
    let at = NORTH;
    for (let i = 0; i < this.shares.length; i++) {
      const turn = this.shares[i] * 2 * Math.PI;
      if (turn > gap) {
        sinks.circles.arc(
          cx, cy, R + f.share_dr, f.share_width, at + gap / 2, turn - gap,
          factionColor(i + 1), f.share_alpha, F_SCREEN,
        );
      }
      at += turn;
    }

    const outer = R + f.share_dr + f.share_width / 2;
    this.answer(sinks, cx, cy, R, outer + f.hover_dr - f.hover_width / 2, f, glow);

    // The words. North over the arcs: the fleur in the historical reading,
    // the N in the modern one. The bearing stands in the lower-right corner.
    const face = faceOf(ctx.reading);
    if (hist) fleur(sinks, ctx, cx, cy, outer, z);
    else {
      this.word(sinks, ctx, cx, cy - (R + f.round_label_dy) / z, z, S.compass_north, 'mono',
        t.text.north, CHART_INK.pale, t.modern.fig.label_alpha, 'centre');
    }
    const brg = hist ? compassPoint(this.home, S.compass_points) : S.compass_home({ brg: formatBearing(this.home) });
    this.word(
      sinks, ctx, cx + (half - f.round_word_inset) / z, cy + (half * f.round_word_dy_k) / z, z, brg, face,
      hist ? t.text.point : t.text.bearing, CHART_INK.bright,
      hist ? t.historical.fig.point_alpha : t.modern.fig.figure_alpha, 'right',
    );
    // The home glyph in the lower-left corner.
    const k = half * f.round_home_k;
    homeGlyph(sinks, cx, cy, z, -k, k, f.round_home_px, f, glow > 0 || roseHovered());

    // The needle mark on the rim, along the bearing to the hold.
    solid(
      sinks, cx, cy, z, screenAngle(this.home), R - f.round_needle_in, R + f.round_needle_out,
      f.round_needle_half, needleFill(f.fill_w, frame.cam.dpr), CHART_INK.bright, f.needle_alpha,
    );
  }

  /**
   * The answer to the rose press (R1758, R1802): under a hover a ring of
   * the accent at `hoverR`; after a press a bright ring on the rim that
   * grows outward as it fades. Under reduced motion it stays on the rim and
   * only fades.
   */
  private answer(
    sinks: ChartSinks, cx: number, cy: number, R: number, hoverR: number,
    f: ChartContext['cfg']['pieces']['compass_rose']['fig'], glow: number,
  ): void {
    if (roseHovered()) {
      sinks.circles.ring(cx, cy, hoverR, f.hover_width, CHART_INK.accent, f.hover_alpha, F_SCREEN);
    }
    if (glow > 0) {
      const r = this.pressStill ? R : R + f.press_grow * (1 - glow);
      sinks.circles.ring(cx, cy, r, f.press_width, CHART_INK.bright, f.press_alpha * glow, F_SCREEN);
    }
  }

  // -- the two readings of the rose alone ---------------------------------

  /** The rim, the eight-point star, the fleur and the point of the compass. */
  private historical(
    s: ChartSinks, ctx: ChartContext, cx: number, cy: number, R: number, z: number,
  ): void {
    const t = ctx.cfg.pieces.compass_rose;
    const r = t.historical, f = r.fig;
    const ink = inkOf(r.ink, CHART_INK.pale);
    s.circles.ring(cx, cy, R, r.width, ink, r.alpha, F_SCREEN);
    // The star's outline runs tip, valley, tip round the rose, and a keel
    // runs from the hub to each tip. The cardinal points are the long ones.
    for (let k = 0; k < POINTS; k++) {
      const a = NORTH + k * STEP;
      const len = R * (k % 2 === 0 ? f.point_k : f.mid_k);
      const next = R * (k % 2 === 0 ? f.mid_k : f.point_k);
      const vr = R * f.valley_k, va = a + STEP / 2;
      const tx = Math.cos(a) * len, ty = Math.sin(a) * len;
      const vx = Math.cos(va) * vr, vy = Math.sin(va) * vr;
      const nx = Math.cos(a + STEP) * next, ny = Math.sin(a + STEP) * next;
      line(s, cx, cy, z, tx, ty, vx, vy, r.width, ink, f.star_alpha);
      line(s, cx, cy, z, vx, vy, nx, ny, r.width, ink, f.star_alpha);
      line(s, cx, cy, z, 0, 0, tx, ty, r.width, ink, f.keel_alpha);
    }
    fleur(s, ctx, cx, cy, R, z);
    // The bearing to the hold as a point of the compass, in the Fell: the
    // historical reading names a course by the point (card 42 does too).
    this.word(
      s, ctx, cx, cy + (R + f.point_dy) / z, z, compassPoint(this.home, S.compass_points), 'fell',
      t.text.point, CHART_INK.bright, f.point_alpha, 'centre',
    );
  }

  /** The rim, the tick ring, the N and the figure. */
  private modern(
    s: ChartSinks, ctx: ChartContext, cx: number, cy: number, R: number, z: number,
  ): void {
    const t = ctx.cfg.pieces.compass_rose;
    const r = t.modern, f = r.fig;
    // The structure is the reader's own accent at a low alpha, as the
    // chrome draws a panel's edge.
    const ink = inkOf(r.ink, factionColor(ctx.state.faction));
    s.circles.ring(cx, cy, R, r.width, ink, r.alpha, F_SCREEN);
    for (let k = 0; k < f.ticks; k++) {
      const a = NORTH + (k * 2 * Math.PI) / f.ticks;
      const major = k % f.major_every === 0;
      const inner = R * (1 - (major ? f.tick_major_k : f.tick_k));
      const cos = Math.cos(a), sin = Math.sin(a);
      line(
        s, cx, cy, z, cos * R, sin * R, cos * inner, sin * inner,
        r.width, ink, major ? f.tick_major_alpha : f.tick_alpha,
      );
    }
    this.word(s, ctx, cx, cy - (R + f.label_dy) / z, z, S.compass_north, 'mono',
      t.text.north, CHART_INK.pale, f.label_alpha, 'centre');
    this.word(
      s, ctx, cx, cy + (R + f.figure_dy) / z, z, S.compass_home({ brg: formatBearing(this.home) }), 'mono',
      t.text.bearing, CHART_INK.bright, f.figure_alpha, 'centre',
    );
  }

  /** One screen word at the world point (x, y), at its chart text step
   *  (R1796): it holds the step's size on the screen at every zoom. */
  private word(
    s: ChartSinks, ctx: ChartContext, x: number, y: number, z: number, text: string,
    face: ChartFace, step: ChartStep,
    tint: number, alpha: number, anchor: 'centre' | 'right',
  ): void {
    ctx.glyphs.run(
      s.quads, text, x, y, face, stepPx(face, step), tint, alpha,
      { anchor, screen: true, zoom: z },
    );
  }
}

/** One straight stroke, its two ends in CSS px about `(cx, cy)`. */
function line(
  s: ChartSinks, cx: number, cy: number, z: number,
  x0: number, y0: number, x1: number, y1: number,
  width: number, tint: number, alpha: number,
): void {
  s.segments.line(cx + x0 / z, cy + y0 / z, cx + x1 / z, cy + y1 / z, width, tint, alpha, F_SCREEN);
}

/**
 * The home glyph (R1802): a house of `HOME_STROKES` strokes, `size` px
 * tall, centred `(gx, gy)` CSS px from `(cx, cy)`. It stands in the dim ink
 * at rest and in the chrome accent while `lit` (a hover or a press).
 */
function homeGlyph(
  s: ChartSinks, cx: number, cy: number, z: number, gx: number, gy: number, size: number,
  f: ChartContext['cfg']['pieces']['compass_rose']['fig'], lit: boolean,
): void {
  const tint = lit ? CHART_INK.accent : CHART_INK.dim;
  const al = lit ? f.home_lit_alpha : f.home_rest_alpha;
  const w = f.home_width;
  // The figures of the glyph, as parts of its size.
  const half = 0.5 * size, eave = -0.02 * size, wall = 0.34 * size, lintel = -0.14 * size;
  // The roof: an eave, the apex, the other eave.
  line(s, cx, cy, z, gx - half, gy + eave, gx, gy - half, w, tint, al);
  line(s, cx, cy, z, gx, gy - half, gx + half, gy + eave, w, tint, al);
  // The two walls and the floor.
  line(s, cx, cy, z, gx - wall, gy + lintel, gx - wall, gy + half, w, tint, al);
  line(s, cx, cy, z, gx - wall, gy + half, gx + wall, gy + half, w, tint, al);
  line(s, cx, cy, z, gx + wall, gy + half, gx + wall, gy + lintel, w, tint, al);
}

/**
 * The width of the strokes that fill the needle (R1760, R1771): `w` CSS px, and
 * never under one device px, so the hairline rule of the segment batch
 * never fades the fill. `dpr` is the device px of one CSS px.
 */
export function needleFill(w: number, dpr: number): number {
  return dpr > 0 ? Math.max(w, 1 / dpr) : w;
}

/**
 * A solid mark along the screen angle `a`, about `(cx, cy)`: its point
 * `tip` px out, its two back corners `base` px out (a negative figure
 * stands behind the centre) and `half` px either side of the axis. The
 * segment batch draws strokes only, so the mark is a fan of `fanLines`
 * strokes from the point to the base (R1745). It takes plain figures and
 * builds no point object, because the rose is live and draws two of these
 * on every frame (§10.3).
 */
function solid(
  s: ChartSinks, cx: number, cy: number, z: number, a: number,
  tip: number, base: number, half: number, w: number, tint: number, alpha: number,
): void {
  const cos = Math.cos(a), sin = Math.sin(a);
  const n = fanLines(2 * half, w);
  // The outer strokes stand half a width inside the back corners.
  const edge = Math.max(0, half - w / 2);
  for (let i = 0; i < n; i++) {
    const off = n === 1 ? 0 : -edge + (2 * edge * i) / (n - 1);
    line(
      s, cx, cy, z, cos * tip, sin * tip,
      cos * base - sin * off, sin * base + cos * off, w, tint, alpha,
    );
  }
}

/**
 * The fleur-de-lis over north, in the faction colour (R1745): a lancet
 * petal, a curl either side of it, the band across them and the foot under
 * the band. The fleur's code point is outside the two vendored subsets
 * (§17.4), so the mark is drawn. Every figure but its height is a part of
 * that height, so one row sizes it. `R` is the circle it stands over: the
 * rim of the rose alone, or the sea share arcs of the round minimap.
 */
function fleur(
  s: ChartSinks, ctx: ChartContext, cx: number, cy: number, R: number, z: number,
): void {
  const r = ctx.cfg.pieces.compass_rose.historical, f = r.fig;
  const tint = factionColor(ctx.state.faction);
  const g = f.fleur_r, w = r.width, al = f.fleur_alpha;
  // The band's height: the foot stops `fleur_dy` over the rim. Up the
  // screen is −y.
  const foot = f.fleur_foot * g;
  const by = -(R + f.fleur_dy + foot);
  // The lancet: band, right shoulder, point, left shoulder, band.
  const pw = f.fleur_petal_w * g, ph = by - f.fleur_petal_at * g, top = by - g;
  line(s, cx, cy, z, 0, by, pw, ph, w, tint, al);
  line(s, cx, cy, z, pw, ph, 0, top, w, tint, al);
  line(s, cx, cy, z, 0, top, -pw, ph, w, tint, al);
  line(s, cx, cy, z, -pw, ph, 0, by, w, tint, al);
  // The band, and the foot under it.
  const band = f.fleur_band * g;
  line(s, cx, cy, z, -band, by, band, by, w, tint, al);
  line(s, cx, cy, z, 0, by, 0, by + foot, w, tint, al);
  // The curls. The right one starts on the petal's side (angle π), turns
  // over the top and down the outside; the left one is its mirror, which
  // is the same sweep ending on the petal's side (angle 0).
  const kx = f.fleur_curl_dx * g, ky = by - f.fleur_curl_dy * g, kr = f.fleur_curl_r * g;
  const sweep = f.fleur_curl_sweep;
  s.circles.arc(cx + kx / z, cy + ky / z, kr, w, Math.PI, sweep, tint, al, F_SCREEN);
  s.circles.arc(cx - kx / z, cy + ky / z, kr, w, -sweep, sweep, tint, al, F_SCREEN);
}

/** The one instance the registry holds. */
export const compassRose = new CompassRose();
